/**
 * ONE place that decides whether a password is acceptable.
 *
 * Written after two independent testers found that contractor signup accepted `abcdef`:
 * `Signup.js` required 6 characters and no complexity, while SetPassword, MyProfile and the
 * customer-portal signup all already required 8. The weakest gate in the product was the one
 * that creates the account — every other surface was stricter than the front door.
 *
 * The rule below is NOT invented here. `api/middleware/inputValidator.js:18` already stated
 * the policy as /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/ — 8+, with an upper, a lower and a
 * digit. That file has ZERO importers, so the policy existed on paper and was enforced
 * nowhere. This module is that same rule made reachable rather than a fifth opinion.
 *
 * 8 is the NIST 800-63B and OWASP floor. Deliberately NOT requiring a special character:
 * NIST 800-63B explicitly advises against composition rules that push users toward
 * predictable substitutions (P@ssw0rd1), and length is the property that actually resists
 * guessing. Upper/lower/digit is kept because it is what the repo already specified.
 *
 * Returns a LIST of problems rather than a boolean so callers can report every objection at
 * once — Signup deliberately collects all validation failures instead of drip-feeding them.
 *
 * NOTE: this is a CLIENT gate. `create-company-with-owner` passes tempPassword straight to
 * auth.admin.createUser with no check of its own, so nothing enforces this server-side yet.
 */

export const MIN_PASSWORD_LENGTH = 8;

/** Human-readable statement of the rule, for hints and placeholder text. */
export const PASSWORD_POLICY_TEXT =
  'At least 8 characters, including an uppercase letter, a lowercase letter and a number.';

/**
 * The rules themselves, so a form can show them being MET rather than only report them
 * broken. Signup stated the policy as one static sentence and said nothing more until
 * submit, which tells you the rules but never whether you have satisfied them — you type,
 * press the button, and find out. A live checklist is what every password field people
 * already use does, and the rules have to come from here or the checklist and the
 * enforcement drift apart the first time one of them is edited.
 *
 * Order matters: it is the order the submit-time messages have always appeared in.
 */
export const PASSWORD_RULES = [
  {
    id: 'length',
    label: `At least ${MIN_PASSWORD_LENGTH} characters`,
    test: (p) => (p || '').length >= MIN_PASSWORD_LENGTH,
    problem: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  },
  {
    id: 'lowercase',
    label: 'A lowercase letter',
    test: (p) => /[a-z]/.test(p || ''),
    problem: 'Password must include a lowercase letter.',
  },
  {
    id: 'uppercase',
    label: 'An uppercase letter',
    test: (p) => /[A-Z]/.test(p || ''),
    problem: 'Password must include an uppercase letter.',
  },
  {
    id: 'number',
    label: 'A number',
    test: (p) => /\d/.test(p || ''),
    problem: 'Password must include a number.',
  },
];

/** Each rule with whether this password satisfies it — for a live checklist. */
export const passwordRuleStatus = (password) =>
  PASSWORD_RULES.map(({ id, label, test }) => ({ id, label, met: test(password) }));

/**
 * Every reason this password is unacceptable. Empty array = acceptable.
 * An empty/absent password returns the "required" problem only, so callers do not stack
 * "too short" on top of "you have not typed anything yet".
 *
 * Derived from PASSWORD_RULES rather than repeating them, so the checklist a user watches
 * and the list that blocks submit cannot disagree.
 */
export const passwordProblems = (password) => {
  if (!password) return ['Password is required.'];
  return PASSWORD_RULES.filter((rule) => !rule.test(password)).map((rule) => rule.problem);
};

/** True when the password satisfies the policy. */
export const isAcceptablePassword = (password) => passwordProblems(password).length === 0;
